const stripHtml = (val?: null | string): string => {
  if (!val) return ''
  const doc = new DOMParser().parseFromString(val, 'text/html')
  return doc.body.textContent ?? ''
}

// Self-contained quote containers (Gmail)
const QUOTE_SELECTORS = ['blockquote', '.gmail_quote', '.gmail_extra'].join(', ')

// Outlook markers
const CUT_ONWARD_SELECTORS = [
  'div#appendonsend', // OWA / new Outlook reply marker
  'div#divRplyFwdMsg', // "From:/Sent:/To:/Subject:" header block
  'div#divRteSpacer', // spacer preceding the quote
  'hr#stopSpelling', // older Outlook separator
].join(', ')

// A trailing node is "empty" if it is a <br>, whitespace, or an element that
// holds no visible text or media (e.g. the "<div><br></div>" Frappe leaves
// before a reply separator).
const isEmptyTrailingNode = (node: Node): boolean => {
  if (!(node instanceof Element)) return (node.textContent ?? '').trim() === ''

  if (node.querySelector('img, video, iframe')) return false

  return (node.textContent ?? '').trim() === ''
}

// Drop trailing empty nodes / line breaks left behind after stripping quotes.
const trimTrailingBreaks = (root: HTMLElement): void => {
  let lastElement = root.lastChild

  while (lastElement && isEmptyTrailingNode(lastElement)) {
    root.removeChild(lastElement)
    lastElement = root.lastChild
  }
}

// Remove the given node, its following siblings, and everything after it
const removeFromNodeOnward = (start: Node, root: Node): void => {
  let currentElement: Node | null = start
  let isStart = true

  while (currentElement && currentElement !== root) {
    while (currentElement.nextSibling) {
      currentElement.parentNode?.removeChild(currentElement.nextSibling)
    }

    const parent: Node | null = currentElement.parentNode

    if (isStart) {
      parent?.removeChild(currentElement)
      isStart = false
    }

    currentElement = parent
  }
}

/**
 * Remove the quoted previous-mail history from an email body so that only the newest message remains.
 */
const stripQuotedReply = (html?: null | string): string => {
  if (!html) return ''

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const { body } = doc

  // 1. Frappe separator "<p>---</p>"
  const separator = Array.from(body.querySelectorAll('p')).find(
    (el) => el.children.length === 0 && el.textContent?.trim() === '---',
  )
  if (separator) removeFromNodeOnward(separator, body)

  // 2. Outlook: cut at the earliest marker
  const cutMarker = body.querySelector(CUT_ONWARD_SELECTORS)
  if (cutMarker) removeFromNodeOnward(cutMarker, body)

  // 3. Gmail: remove self-contained quote containers
  body.querySelectorAll(QUOTE_SELECTORS).forEach((el) => {
    el.remove()
  })

  // 4. Trailing breaks
  trimTrailingBreaks(body)

  const result = body.innerHTML.trim()

  // If stripping removed everything (e.g. the body was purely a quote), keep the
  // original content rather than showing an empty preview
  return result || html
}

export { stripHtml, stripQuotedReply }
